import { Avatar, Card, Grid, Group, Stack, Text, UnstyledButton } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GET_INSTAGRAM_MEDIA } from "../../services/instagram_analytics";
import {
  AnalyticsEmptyState,
  InstagramAnalyticsHeader,
  MediaTypeBadge,
  formatDate,
  formatNumber,
  getSyncResultMessage,
  resolveMediaLatestMetrics,
  useInstagramAccounts,
  useInstagramSync,
} from "./shared";

export default function InstagramAnalyticsMediaPage() {
  const navigate = useNavigate();
  const { oauthId } = useParams();
  const { accounts, loadingAccounts } = useInstagramAccounts();

  const [loading, setLoading] = React.useState<boolean>(true);
  const [media, setMedia] = React.useState<any[]>([]);

  const loadData = React.useCallback(async () => {
    if (!oauthId) return;

    try {
      setLoading(true);
      const response = await GET_INSTAGRAM_MEDIA(oauthId);
      const rows = response?.media || [];
      setMedia(rows);
      getSyncResultMessage(rows.length === 0);
    } catch (err: any) {
      showNotification({ message: err.message, color: "red" });
    } finally {
      setLoading(false);
    }
  }, [oauthId]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const { syncing, triggerSync } = useInstagramSync(oauthId, loadData);

  const isEmpty = !loading && media.length === 0;

  return (
    <Stack>
      <InstagramAnalyticsHeader
        oauthId={oauthId}
        title="Mídias / Posts"
        subtitle="Métricas mais recentes por publicação"
        active="media"
        accounts={accounts}
        loadingAccounts={loadingAccounts}
        syncing={syncing}
        onSync={triggerSync}
      />

      {isEmpty ? (
        <AnalyticsEmptyState oauthId={oauthId} syncing={syncing} onSync={triggerSync} />
      ) : (
        <Grid>
          {media.map((post) => {
            const latestMetrics = resolveMediaLatestMetrics(post);
            return (
            <Grid.Col key={post._id || post.ig_media_id} md={4}>
              <UnstyledButton
                style={{ width: "100%" }}
                onClick={() => navigate(`/analytics/instagram/${oauthId}/media/${post._id || post.ig_media_id}`)}
              >
                <Card withBorder h="100%">
                  <Card.Section>
                    <Avatar
                      src={post.media_url}
                      radius={0}
                      size={220}
                      style={{ width: "100%", height: 220, objectFit: "cover" }}
                    />
                  </Card.Section>

                  <Group position="apart" mt="sm" mb="xs">
                    <MediaTypeBadge type={post.media_type} />
                    <Text size="xs" c="dimmed">
                      {formatDate(post.timestamp)}
                    </Text>
                  </Group>

                  <Text size="sm" lineClamp={2} mb="sm">
                    {post.caption || "Sem legenda"}
                  </Text>

                  <Grid gutter={6}>
                    <Grid.Col span={6}>
                      <Text size="xs" c="dimmed">Curtidas</Text>
                      <Text fw={600}>{formatNumber(latestMetrics.likes)}</Text>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Text size="xs" c="dimmed">Comentários</Text>
                      <Text fw={600}>{formatNumber(latestMetrics.comments)}</Text>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Text size="xs" c="dimmed">Salvamentos</Text>
                      <Text fw={600}>{formatNumber(latestMetrics.saves)}</Text>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Text size="xs" c="dimmed">Impressões</Text>
                      <Text fw={600}>{formatNumber(latestMetrics.impressions)}</Text>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Text size="xs" c="dimmed">Alcance</Text>
                      <Text fw={600}>{formatNumber(latestMetrics.reach)}</Text>
                    </Grid.Col>
                  </Grid>
                </Card>
              </UnstyledButton>
            </Grid.Col>
            );
          })}
        </Grid>
      )}
    </Stack>
  );
}
